import { useDomain } from "context/DomainContext";
import { t } from "i18next";
import { Attachment, FinalCost, Material, Province } from "types/canvasTypes";
import { MaterialNameMap } from "types/materialList";

type QuotationTableProps = {
  province: Province;
  selectedLength: number;
  finalCost: FinalCost[];
  materialOption: string;
  material: Attachment[];
  totalCost: number;
  editableAttachments?: boolean;
  onAttachmentCountChange?: (material: Material, delta: number) => void;
};

const QuotationTable = ({
  province,
  selectedLength,
  finalCost,
  materialOption,
  material,
  totalCost,
  editableAttachments = false,
  onAttachmentCountChange,
}: QuotationTableProps) => {
  const { showPrices } = useDomain();
  const canEdit = editableAttachments && !!onAttachmentCountChange;
  const activeAttachments = material.filter((item) => item.count > 0);
  const zeroCountAttachments = canEdit
    ? material.filter((item) => item.count === 0)
    : [];
  const showPriceColumns = showPrices;
  const columnCount = showPriceColumns ? 4 : 2;

  return (
    <div id="sidesInformation">
      <div>
        <table className="quotation-table">
          <tbody>
            <tr>
              <th>
                <div className="table-headline">{t("glass")}</div>
              </th>
              <th>{t("quantity")}</th>
              {showPriceColumns && <th>{t("pricePerUnit")}</th>}
              {showPriceColumns && <th style={{ textAlign: "end" }}>total</th>}
            </tr>

            {finalCost.map((cost, index) => {
              return (
                cost.total > 0 && (
                  <tr key={index}>
                    <td>
                      {cost.isDoor ? "Door" : ""}
                      {selectedLength} x {cost.length}"
                      {cost.isRPannel ? "R" : ""}
                    </td>
                    <td>{cost.total}</td>
                    {showPriceColumns && <td>{cost.price.get(province)!}$</td>}
                    {showPriceColumns && (
                      <td style={{ textAlign: "end" }}>
                        {cost.price.get(province)! * cost.total}$
                      </td>
                    )}
                  </tr>
                )
              );
            })}
            <tr>
              <td colSpan={columnCount}>
                <div className="table-headline">
                  Material
                  <span
                    style={{
                      fontSize: "15px",
                      color: "var(--color-text)",
                      fontWeight: 400,
                    }}
                  >
                    ({materialOption})
                  </span>
                </div>
              </td>
            </tr>
            {activeAttachments.map((material) => (
              <tr key={material.material}>
                <td>
                  {MaterialNameMap.get(material.material)?.get(province)}
                </td>
                <td>
                  {canEdit ? (
                    <div className="attachment-quantity-control">
                      <button
                        type="button"
                        className="attachment-quantity-button"
                        aria-label={`Remove ${material.material}`}
                        onClick={() => onAttachmentCountChange(material.material, -1)}
                        disabled={material.count <= 0}
                      >
                        -
                      </button>
                      <span>{material.count}</span>
                      <button
                        type="button"
                        className="attachment-quantity-button"
                        aria-label={`Add ${material.material}`}
                        onClick={() => onAttachmentCountChange(material.material, 1)}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    material.count
                  )}
                </td>
                {showPriceColumns && (
                  <td>
                    {materialOption === "Stainless"
                      ? material.costSilver.get(province)!
                      : material.costBlack.get(province)!}
                    $
                  </td>
                )}
                {showPriceColumns && (
                  <td style={{ textAlign: "end" }}>
                    {material.count *
                      (materialOption === "Stainless"
                        ? material.costSilver.get(province) || 0
                        : material.costBlack.get(province) || 0)}
                    $
                  </td>
                )}
              </tr>
            ))}
            {canEdit && zeroCountAttachments.length > 0 && (
              <tr>
                <td colSpan={columnCount}>
                  <div className="table-headline">{t("zeroQuantityAttachments")}</div>
                </td>
              </tr>
            )}
            {canEdit && zeroCountAttachments.map((material) => (
              <tr key={`${material.material}-zero`}>
                <td>
                  {MaterialNameMap.get(material.material)?.get(province)}
                </td>
                <td>
                  <div className="attachment-quantity-control">
                    <button
                      type="button"
                      className="attachment-quantity-button"
                      aria-label={`Remove ${material.material}`}
                      onClick={() => onAttachmentCountChange(material.material, -1)}
                      disabled={material.count <= 0}
                    >
                      -
                    </button>
                    <span>{material.count}</span>
                    <button
                      type="button"
                      className="attachment-quantity-button"
                      aria-label={`Add ${material.material}`}
                      onClick={() => onAttachmentCountChange(material.material, 1)}
                    >
                      +
                    </button>
                  </div>
                </td>
                {showPriceColumns && (
                  <td>
                    {materialOption === "Stainless"
                      ? material.costSilver.get(province)!
                      : material.costBlack.get(province)!}
                    $
                  </td>
                )}
                {showPriceColumns && <td style={{ textAlign: "end" }}>0$</td>}
              </tr>
            ))}
            {showPriceColumns && (
              <tr>
                <td colSpan={4}>
                  <div className="table-headline">Total</div>
                </td>
              </tr>
            )}

            {showPriceColumns && (
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    textAlign: "end",
                  }}
                >
                  {totalCost}$ + tx
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuotationTable;
